import { useState } from '~renderer/composables/useState'
import { Logger } from '~shared/core/Logger'
import type { ApiErrorPayload } from '~shared/types/error'
import { isErrorPayload } from '~shared/util/error'

type OnErrorHandler = ApiErrorPayload | ((error: ApiErrorPayload) => ApiErrorPayload)

const processErrorPayload = (errorPayload: ApiErrorPayload, onError?: OnErrorHandler) => {
  const { addError } = useState()
  const _errorPayload = onError
    ? typeof onError === 'function'
      ? onError(errorPayload)
      : onError
    : errorPayload

  switch (_errorPayload.severity) {
    case 'error':
      logger.group('ERROR').error(_errorPayload)
      addError(_errorPayload.code, _errorPayload.message, 'error')
      break
    case 'warn':
      logger.group('WARN').warn(_errorPayload)
      addError(_errorPayload.code, _errorPayload.message, 'warn')
      break
    case 'silent':
    default:
      break
  }
}

/**
 * Guard: Frontend Telefunc Wrapper
 * Intercepts the response from a Telefunc call.
 * If the response is an implementation of ApiErrorPayload (TurnipExchangeError), it throws it.
 * This ensures that useQuery (or any async handler) sees it as a failure.
 *
 */
const logger = new Logger('Telefunc Guard')
export async function guard<T, E = ApiErrorPayload>(
  promise: Promise<T>,
  { onError }: { onError?: OnErrorHandler } = {},
): Promise<Exclude<T, E>> {
  const { addError } = useState()
  let isErrorProcessed = false

  try {
    const result = await promise

    if (isErrorPayload(result)) {
      processErrorPayload(result, onError)

      isErrorProcessed = true
      throw result
    }

    return result as Exclude<T, E>
  } catch (err: unknown) {
    if (!isErrorProcessed && isErrorPayload(err)) processErrorPayload(err, onError)
    if (isErrorProcessed) throw err

    logger.group('SYSTEM FAULT').error(err)
    addError('unknown_error', 'Something went wrong. Please try again.', 'error')

    throw err
  }
}
